import { CustomPageType, HeaderCustomization } from '@ViewModels';
import { css } from 'aphrodite';
import { CSSProperties, FC } from 'react';
import { useGetBlogStylesByTypeQuery, useSchedulerPreviewCustomPageStyleQuery } from '../../../../queries';
import { IMeetingLandingCard, MeetingLandingBody } from '../../../../scheduleMeeting/components/MeetingLandingBody';
import { MeetingContainerView } from '../../../../scheduleMeeting/containers/MeetingContainer/MeetingContainerView';
import { SCHEDULER_ACCENT_VARS } from '../../../../scheduleMeeting/styles/customBrandedAccents';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { bs } from '../../../../web/styles/styles';
import { Column, Row } from '../../Layout';
import { ShadowHtml } from '../../ShadowHtml';
import { useAccountContext } from '../AccountContext';
import { Divider } from '../presentation';
import { styleSheet as blogStyles } from '../styles';
import { styleSheet } from './styles';

const SAMPLE_MEETING_CONFIGS: IMeetingLandingCard[] = [
	{ name: '30 minute meeting', duration: '00:30:00', shortCode: 'sample-30-minute', vanityPath: 'preview' },
	{ name: '60 minute meeting', duration: '01:00:00', shortCode: 'sample-60-minute', vanityPath: 'preview' },
	{ name: 'Phone consultation', duration: '01:00:00', shortCode: 'sample-phone', vanityPath: 'preview' },
	{ name: 'In-person meeting', duration: '01:00:00', shortCode: 'sample-in-person', vanityPath: 'preview' },
];

const SAMPLE_HOST_NAME = 'John Smith';

const renderShadowHtml = (html: string, htmlClassName: string) => {
	return <ShadowHtml className={htmlClassName} html={html} />;
};

// Mirrors the live meeting page, which sets these --custom-* variables at :root (backend
// _SurveyBaseStyles.cshtml) for the components-v2 Button to read. The preview renders the landing
// body in the light DOM, so the header embed's shadow-scoped styles can't reach it — set them here.
const buildCustomThemeVars = (primaryButton?: HeaderCustomization['primaryButton']): CSSProperties => {
	const vars: Record<string, string> = {};
	if (primaryButton?.backgroundColor) {
		vars['--custom-primary-button-bg'] = primaryButton.backgroundColor;
	}
	if (primaryButton?.color) {
		vars['--custom-primary-button-text'] = primaryButton.color;
	}
	if (primaryButton?.hoverColor) {
		vars['--custom-primary-button-hover'] = primaryButton.hoverColor;
	}
	if (Object.keys(vars).length) {
		// Custom button colors also flip the scheduler's blue accents to neutral grays, mirroring
		// what MeetingContainer sets on document.body on the live page.
		Object.assign(vars, SCHEDULER_ACCENT_VARS);
	}
	return vars as CSSProperties;
};

export const MeetingPagePreview: FC = () => {
	const { account, impersonationContext } = useAccountContext();

	const headerQuery = useSchedulerPreviewCustomPageStyleQuery({
		impersonationContext,
		type: CustomPageType.Header,
	});
	const footerQuery = useSchedulerPreviewCustomPageStyleQuery({
		impersonationContext,
		type: CustomPageType.Footer,
	});

	const headerStyleQuery = useGetBlogStylesByTypeQuery({
		impersonationContext,
		type: CustomPageType.Header,
		meta: {
			toastErrorMessage: 'Failed to load part of the meeting page preview',
		},
	});
	const customThemeVars = buildCustomThemeVars(
		headerStyleQuery.data?.type === CustomPageType.Header ? headerStyleQuery.data.primaryButton : undefined
	);

	const isPending = headerQuery.isPending || footerQuery.isPending || headerStyleQuery.isPending;
	// a failed fetch renders like absence (the toast from query meta reports the failure)
	const headerHtml = headerQuery.data?.hasCustomization ? headerQuery.data.html : undefined;
	const footerHtml = footerQuery.data?.hasCustomization ? footerQuery.data.html : undefined;

	return (
		<Column className={css(blogStyles.gap12)}>
			<h2 className={css(blogStyles.subtitleText)}>Meeting Booking Page Preview</h2>
			<p className={css(styleSheet.caption)}>Desktop layout · sample meeting types</p>
			<Divider />
			{isPending ? (
				<Row className={css(bs.p7, bs.justifyCenter)}>
					<LoadingSpinner />
				</Row>
			) : (
				<div className={css(styleSheet.canvas)} style={customThemeVars}>
					<MeetingContainerView
						customFooterHtml={footerHtml}
						customHeaderHtml={headerHtml}
						renderCustomHtml={renderShadowHtml}
						wrapperStyles={[styleSheet.previewWrapper]}
					>
						<MeetingLandingBody
							companyName={account?.companyName || 'Your Company'}
							creatorName={SAMPLE_HOST_NAME}
							hasCustomHeaderOrFooter={!!headerHtml || !!footerHtml}
							meetingConfigs={SAMPLE_MEETING_CONFIGS}
						/>
					</MeetingContainerView>
				</div>
			)}
		</Column>
	);
};
